import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from "prop-types";
import appConfig from "services/config";
import { httpGetMyUser } from "services/users";
import { revokeRefreshToken } from "services/api";
import { useStoreState, useStoreActions } from 'easy-peasy';
import i18n, { syncMomentLocale } from "services/i18n";
import { Spinner } from "stories/components";
import { Container, Row, Col } from "stories/layout";

const SUPPORTED_UI_LANGS = ["fi", "en", "sv"];

function normalizeUiLanguage(code) {
  if (code == null || code === "") return null;
  const s = String(code).trim().toLowerCase().slice(0, 2);
  return SUPPORTED_UI_LANGS.includes(s) ? s : null;
}

export const AuthContext = createContext({});

// Create a wrapper component that uses hooks
const AuthProviderWithRouter = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  return <AuthProviderComponent location={location} navigate={navigate}>{children}</AuthProviderComponent>;
};

const AuthProviderComponent = ({ location, navigate, children }) => {
  const [loading, setLoading] = useState(true);
  const [loadingMyUser, setLoadingMyUser] = useState(false);
  const [loadingIsAuthenticated, setLoadingIsAuthenticated] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accountUserType, setAccountUserType] = useState();
  const [userRole, setUserRole] = useState(null);

  const [myUser, setMyUser] = useState({});
  const getAccount = useStoreActions((actions) => actions.account.getAccount);
  const getPools = useStoreActions((actions) => actions.pools.getPools);
  
  const removeTokens = () => {
    console.log("AuthContext:removeTokens");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }
  
  const logout = () => {
    console.log("AuthContext:logout");
    revokeRefreshToken().finally(() => {
      removeTokens();
      window.location.reload();
    });
  }
  
  const getCurrentLanguage = () => {
    return localStorage.getItem("language") || "fi";
  }
  
  const setLanguage = (newLanguage) => {
    const lang = normalizeUiLanguage(newLanguage) || "fi";
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
    syncMomentLocale(lang);
  };

  const getMyUser = () => {
    return new Promise((resolve, reject) => {
      // getAccount()
      // legacy account fetch removed
      // getUnmadeSplitTransactions()
      
      if (appConfig?.TEST_NO_BACKEND === true) {
        setIsAuthenticated(true);
        setLoading(false);
        return resolve({id:1})
      }
      
      if (location?.pathname.startsWith("/auth/invitation")) {
        setLoading(false);
        setIsAuthenticated(false);
        return resolve(null);
      }
      
      if (localStorage.getItem("accessToken") === null) {
        setIsAuthenticated(false);
        setLoading(false);
        return resolve(null);
      }
      else {
        setLoading(true);
        setLoadingMyUser(true)

        // Chain the API calls properly
        httpGetMyUser()
          .then(response => {
            if (response && response.status === 200) {
              // Load necessary data when user logs in
              getAccount()
              getPools()
              // legacy account fetch removed
              // getUnmadeSplitTransactions()

              setMyUser(response?.data);
              //reload profile picture even if it is not changed

              setIsAuthenticated(true);
              
              // Set user role
              setUserRole(response?.data?.role);
              console.log('AuthContext: User role received:', response?.data?.role);
              console.log('AuthContext: Full user data:', response?.data);
              console.log('AuthContext: Role type:', typeof response?.data?.role);
              console.log('AuthContext: Is role 3?', response?.data?.role === 3);
              console.log('AuthContext: Is role === 3?', response?.data?.role === 3);
              console.log('AuthContext: Is role == 3?', response?.data?.role == 3);
              
              // Prefer profile language from API over localStorage. Storing "fi" in localStorage from an
              // earlier session used to win over profile "en", so the UI stayed Finnish incorrectly.
              const serverLang = normalizeUiLanguage(response?.data?.language);
              const storedLang = normalizeUiLanguage(localStorage.getItem("language"));
              const effectiveLang = serverLang || storedLang || "fi";
              setLanguage(effectiveLang);
              
              // Now fetch account user data
              resolve(response?.data);
            }
            else {
              setIsAuthenticated(false);
              return Promise.resolve(null);
            }
          })
          .catch(error => {
            console.log("Error fetching user data:", error);
            
            if (error && error?.status === 400) {
              alert(JSON.stringify(error?.data));
                          // Removed account_user check since backend no longer supports account_users
            }
            
            setIsAuthenticated(false);
            setLoadingMyUser(false);
            resolve(null);
          })
          .finally(() => {
            setLoading(false);
            setLoadingMyUser(false);
          });
      }
    });
  }
  
  useEffect(() => {
    getMyUser()
  }, [])
  
  if (loading ) {
    return (
     
          <Container className="d-flex align-items-center justify-content-center" style={{ height: "100vh" }}>
          <Spinner animation="border" role="status" className="text-secondary"/>
          </Container>

    )
  }
  
  return (
      <AuthContext.Provider
        value={{
          getMyUser,
          isAuthenticated,
          isAdmin,
          loading,
          logout,
          myUser,
          setLanguage,
          getCurrentLanguage,
          removeTokens,
          loadingMyUser,
          accountUserType,
          userRole,
          token: localStorage.getItem("accessToken"), // Add token for WebSocket
        }}
      >
        {children}
      </AuthContext.Provider>
    );
};

// Use AuthProviderWithRouter component instead of withRouter
export const AuthProvider = AuthProviderWithRouter;

AuthContext.defaultProps = {};
AuthContext.propTypes = {
  children: PropTypes.node.isRequired
};

export const useAuthContext = () =>
  useContext(AuthContext);
  
/*
# USAGE

import { useAuthContext } from "contexts/AuthContext"
const MyComponent = (props) => {
  const { myUser } = useAuthContext()
}
*/
